import { useCallback, useEffect, useState } from 'react';

/**
 * Per-localStorage-key "has the user seen X yet?" flag.
 *
 * Uses a namespace prefix so multiple tours/educations can coexist
 * without colliding. `markSeen` is idempotent + synchronous for
 * localStorage (good enough — tests don't worry about SSR).
 *
 * Safe on SSR / storage-less environments (e.g. tests): falls back to
 * in-memory state, so calling it won't throw.
 */
const PREFIX = 'ubc-first-visit:';

function safeGet(key: string): boolean {
  try {
    return localStorage.getItem(PREFIX + key) === '1';
  } catch {
    return false;
  }
}

function safeSet(key: string): void {
  try {
    localStorage.setItem(PREFIX + key, '1');
  } catch {
    // storage unavailable — fall through to in-memory only
  }
}

function safeClear(key: string): void {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    // ignore
  }
}

export function useFirstVisitFlag(key: string): {
  seen: boolean;
  markSeen: () => void;
  clear: () => void;
} {
  const [seen, setSeen] = useState<boolean>(() => safeGet(key));

  // Re-read once after mount in case storage availability was ambiguous
  // initially (e.g. effects only on client).
  useEffect(() => {
    setSeen(safeGet(key));
  }, [key]);

  const markSeen = useCallback(() => {
    safeSet(key);
    setSeen(true);
  }, [key]);

  const clear = useCallback(() => {
    safeClear(key);
    setSeen(false);
  }, [key]);

  return { seen, markSeen, clear };
}
